import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { money } from "../lib/constants";
import { ShieldAlert, AlertTriangle, ClipboardList, ListChecks, DollarSign, ChevronRight } from "lucide-react";

export default function Compliance() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [view, setView] = useState("risks");
  const [highOnly, setHighOnly] = useState(false);

  useEffect(() => { api.get("/compliance").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[#5A687C]">Loading compliance view…</div>;

  const t = d.tiles;
  const sev = (r) => (r.likelihood || 0) * (r.impact || 0);
  const codeOf = (id) => d.workstreams.find((w) => w.id === id)?.code || "";

  const TILES = [
    { key: "workstreams", label: "In-scope workstreams", val: t.workstreams, color: "#BA0C2F", icon: ShieldAlert, drill: "workstreams", high: false },
    { key: "open_risks", label: "Open risks", val: t.open_risks, color: "#C97A1B", icon: AlertTriangle, drill: "risks", high: false },
    { key: "high_risks", label: "High-severity risks", val: t.high_risks, color: "#BA0C2F", icon: AlertTriangle, drill: "risks", high: true },
    { key: "open_findings", label: "Open findings", val: t.open_findings, color: "#BA0C2F", icon: ClipboardList, drill: "findings", high: false },
    { key: "open_tasks", label: "Open action items", val: t.open_tasks, color: "#BA0C2F", icon: ListChecks, drill: "tasks", high: false },
    { key: "budget", label: "Security & Ops budget", val: money(t.budget_planned), color: "#2E8B57", icon: DollarSign, drill: "budget", high: false },
  ];

  const risks = (highOnly ? d.raid.filter((r) => sev(r) >= 12) : d.raid).slice().sort((a, b) => sev(b) - sev(a));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold text-[#BA0C2F] uppercase tracking-wider">Audit &amp; board review</div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733] mt-1">Compliance &amp; Risk Spotlight</h1>
        <p className="text-[#5A687C] mt-1 max-w-3xl leading-relaxed">
          Security and IT Operations risks, findings, and remediation in one board-ready view. Select any tile to drill into the underlying detail.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="compliance-tiles">
        {TILES.map((tile) => {
          const Icon = tile.icon;
          const active = view === tile.drill && highOnly === tile.high;
          return (
            <button key={tile.key} data-testid={`compliance-tile-${tile.key}`}
              onClick={() => { setView(tile.drill); setHighOnly(tile.high); }}
              className={`text-left bg-white rounded-[18px] border p-5 transition-all hover:-translate-y-0.5 ${active ? "border-[#BA0C2F] ring-2 ring-[#BA0C2F]/30 shadow-md" : "border-[#DCE2EA]"}`}>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-extrabold" style={{ color: tile.color }}>{tile.val}</span>
                <Icon className="h-5 w-5" style={{ color: tile.color }} />
              </div>
              <div className="text-xs text-[#5A687C] mt-1 font-medium">{tile.label}</div>
            </button>
          );
        })}
      </div>

      {view === "workstreams" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="compliance-detail-workstreams">
          {d.workstreams.map((w) => (
            <button key={w.id} onClick={() => nav(`/workstream/${w.id}`)}
              className="text-left bg-white rounded-[18px] border border-[#DCE2EA] p-6 hover:shadow-md transition-all">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-xs font-bold text-white bg-[#BA0C2F] rounded-full px-2.5 py-1">{w.code}</span>
                  <h3 className="text-lg font-bold text-[#1F2733] mt-2">{w.title}</h3>
                </div>
                <StatusBadge status={w.status} />
              </div>
              <p className="text-sm text-[#5A687C] mt-2">{w.description}</p>
              <div className="mt-3 text-sm font-semibold text-[#BA0C2F] flex items-center gap-1">Open workstream <ChevronRight className="h-4 w-4" /></div>
            </button>
          ))}
        </div>
      )}

      {view === "risks" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] overflow-hidden overflow-x-auto" data-testid="compliance-detail-risks">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#DCE2EA]">
            <h2 className="font-bold text-[#1F2733]">{highOnly ? "High-severity risks" : "Open & tracked risks"}</h2>
            <span className="text-xs text-[#98A2AF]">{risks.length} shown</span>
          </div>
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="bg-[#F4F6FA] text-[#5A687C] text-xs font-bold uppercase tracking-wider">
              <th className="text-left px-4 py-3">WS</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Description</th>
              <th className="text-center px-4 py-3">L×I</th><th className="text-left px-4 py-3">Mitigation plan</th><th className="text-left px-4 py-3">Status</th>
            </tr></thead>
            <tbody>
              {risks.map((r) => (
                <tr key={r.id} onClick={() => nav(`/workstream/${r.workstream_id}`)} className="border-b border-[#DCE2EA]/60 hover:bg-[#F4F6FA]/60 cursor-pointer">
                  <td className="px-4 py-3 font-bold text-[#BA0C2F]">{r.ws_code || codeOf(r.workstream_id)}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold uppercase px-2.5 py-1 rounded-full bg-[#FBEAED] text-[#8A0822]">{r.type}</span></td>
                  <td className="px-4 py-3 max-w-xs">{r.description}</td>
                  <td className="px-4 py-3 text-center font-bold" style={{ color: sev(r) >= 15 ? "#BA0C2F" : sev(r) >= 8 ? "#C97A1B" : "#2E8B57" }}>{r.likelihood}×{r.impact}</td>
                  <td className="px-4 py-3 text-[#5A687C] max-w-xs">{r.mitigation_plan}</td>
                  <td className="px-4 py-3">{r.status}</td>
                </tr>
              ))}
              {risks.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5A687C]">No risks in scope.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "findings" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 space-y-2" data-testid="compliance-detail-findings">
          <h2 className="font-bold text-[#1F2733] mb-2">Findings</h2>
          {d.findings.map((f) => (
            <button key={f.id} onClick={() => nav(`/workstream/${f.workstream_id}`)} className="w-full text-left flex items-start gap-3 p-3 rounded-xl hover:bg-[#F4F6FA]">
              <span className="text-xs font-extrabold text-white bg-[#BA0C2F] rounded-full h-7 w-7 flex items-center justify-center shrink-0">F{f.index}</span>
              <div className="flex-1"><div className="text-sm text-[#1F2733]">{f.text}</div><div className="text-[11px] text-[#98A2AF] mt-0.5">{f.ws_code || codeOf(f.workstream_id)} · {f.status}</div></div>
            </button>
          ))}
        </div>
      )}

      {view === "tasks" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 space-y-2" data-testid="compliance-detail-tasks">
          <h2 className="font-bold text-[#1F2733] mb-2">Action items</h2>
          {d.tasks.map((tk) => (
            <div key={tk.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F6FA]">
              <span className="text-xs font-bold text-[#5A687C] w-16">{tk.ws_code || codeOf(tk.workstream_id)}</span>
              <div className="flex-1 min-w-0"><div className="text-sm text-[#1F2733] truncate">{tk.title}</div><div className="text-[11px] text-[#98A2AF]">{tk.start_date} → {tk.end_date}</div></div>
              <span className="text-xs font-semibold text-[#5A687C]">{tk.status}</span>
            </div>
          ))}
        </div>
      )}

      {view === "budget" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6" data-testid="compliance-detail-budget">
          <h2 className="font-bold text-[#1F2733] mb-3">Security &amp; IT Operations budget</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-[#5A687C] text-xs font-bold uppercase border-b border-[#DCE2EA]"><th className="text-left py-2">WS</th><th className="text-left py-2">Category</th><th className="text-right py-2">Planned</th><th className="text-right py-2">Actual</th></tr></thead>
            <tbody>
              {d.budget.map((b) => (
                <tr key={b.id} className="border-b border-[#DCE2EA]/60"><td className="py-2 font-bold text-[#BA0C2F]">{b.ws_code || codeOf(b.workstream_id)}</td><td className="py-2">{b.category}</td><td className="py-2 text-right">{money(b.planned_amount)}</td><td className="py-2 text-right">{money(b.actual_amount)}</td></tr>
              ))}
              <tr className="font-bold"><td className="py-2" colSpan={2}>Total</td><td className="py-2 text-right">{money(t.budget_planned)}</td><td className="py-2 text-right">{money(t.budget_actual)}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
